import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Newspaper, CalendarRange, History, Archive, Calculator, ReceiptText, ScrollText,
  Bell, Upload, FolderPlus, File as FileIcon, Folder, Download, Trash2, ImagePlus, X, ChevronRight,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { FileThumb, FilePreviewModal, driveFileUrl } from "@/components/FilePreviewModal";

export const TABS = [
  { k: "actualite", label: "Actualité", icon: Newspaper },
  { k: "exercice", label: "Exercice en cours", icon: CalendarRange },
  { k: "exercice_n1", label: "Exercice N-1", icon: History },
  { k: "archives", label: "Archives", icon: Archive },
  { k: "comptabilite", label: "Comptabilité", icon: Calculator },
  { k: "facturation", label: "Factures", icon: ReceiptText },
  { k: "fiches", label: "Fiches perso.", icon: ScrollText },
];

const token = () => localStorage.getItem("l30m_token");

function humanSize(b) {
  if (!b) return "";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function FileManager({ clientId = null, isAdmin = false, fullscreenBg = false }) {
  const [tab, setTab] = useState("actualite");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bgId, setBgId] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [driveItems, setDriveItems] = useState([]);
  const [drivePath, setDrivePath] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [search, setSearch] = useState("");
  const fileInput = useRef(null);
  const bgInput = useRef(null);
  const folderInput = useRef(null);
  const [portalFolder, setPortalFolder] = useState(null); // dossier de dépôt ouvert {id, name} ou null
  const [uploadLabel, setUploadLabel] = useState(null);

  const withCid = useCallback(
    (base) => (clientId ? `${base}${base.includes("?") ? "&" : "?"}client_id=${clientId}` : base),
    [clientId]
  );

  const loadFiles = useCallback((cat) => {
    setLoading(true);
    api.get(withCid(`/portal/files?category=${cat}`))
      .then((r) => setFiles(r.data.files))
      .catch(() => toast.error("Chargement impossible."))
      .finally(() => setLoading(false));
  }, [withCid]);

  const loadNotifs = useCallback(() => {
    api.get(withCid("/portal/notifications")).then((r) => {
      setNotifs(r.data.notifications); setUnread(r.data.unread);
    }).catch(() => {});
  }, [withCid]);

  const loadBg = useCallback(() => {
    api.get(withCid("/portal/settings")).then((r) => setBgId(r.data.background_file_id)).catch(() => {});
  }, [withCid]);

  const loadDrive = useCallback((opts) => {
    setDriveLoading(true);
    const q = opts.folderId ? `folder_id=${opts.folderId}` : `category=${opts.category}`;
    api.get(withCid(`/portal/drive?${q}`)).then((r) => setDriveItems(r.data.items || []))
      .catch(() => setDriveItems([])).finally(() => setDriveLoading(false));
  }, [withCid]);
  const openDriveFolder = (item) => { setDrivePath((p) => [...p, { id: item.id, name: item.name }]); loadDrive({ folderId: item.id }); };
  const driveJumpTo = (index) => {
    if (index < 0) { setDrivePath([]); loadDrive({ category: tab }); return; }
    const np = drivePath.slice(0, index + 1); setDrivePath(np); loadDrive({ folderId: np[np.length - 1].id });
  };

  useEffect(() => { loadBg(); loadNotifs(); setTab("actualite"); }, [loadBg, loadNotifs]);
  useEffect(() => { loadFiles(tab); setDrivePath([]); loadDrive({ category: tab }); setSearch(""); setPortalFolder(null); }, [tab, loadFiles, loadDrive]);

  const uploadFiles = async (list) => {
    if (!list || !list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", tab);
        if (clientId) fd.append("client_id", clientId);
        await api.post("/portal/upload", fd);
      }
      const dest = TABS.find((t) => t.k === (tab === "actualite" || isAdmin ? tab : "actualite")).label;
      toast.success(`${list.length} dépôt(s) ajouté(s) dans « ${dest} ».`);
      loadFiles(tab); loadNotifs();
    } catch { toast.error("Échec de l'envoi."); } finally { setUploading(false); }
  };

  const createFolder = async () => {
    folderInput.current?.click();
  };

  // Dépôt d'un dossier local complet : crée le dossier dans l'application puis envoie tout son contenu.
  const uploadFolder = async (list) => {
    if (!list || !list.length) return;
    const folderName = (list[0].webkitRelativePath || "").split("/")[0] || "Dossier";
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("is_folder", "true"); fd.append("folder_name", folderName); fd.append("category", tab);
      if (clientId) fd.append("client_id", clientId);
      const r = await api.post("/portal/upload", fd);
      const folderId = r.data.id;
      let done = 0;
      for (const f of list) {
        done += 1;
        setUploadLabel(`Envoi ${done}/${list.length}…`);
        const fdd = new FormData();
        fdd.append("file", f, f.name);
        fdd.append("category", tab);
        fdd.append("folder_id", folderId);
        fdd.append("notify", "false"); // une seule notification : celle du dossier
        if (clientId) fdd.append("client_id", clientId);
        await api.post("/portal/upload", fdd);
      }
      toast.success(`Dossier « ${folderName} » déposé (${list.length} fichier${list.length > 1 ? "s" : ""}).`);
      loadFiles(tab); loadNotifs();
    } catch { toast.error("Échec de l'envoi du dossier."); loadFiles(tab); } finally { setUploading(false); setUploadLabel(null); }
  };

  const uploadBackground = async (f) => {
    if (!f || !clientId) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("client_id", clientId);
    try {
      const r = await api.post("/portal/background", fd);
      setBgId(r.data.file_id);
      toast.success("Visuel de fond mis à jour.");
    } catch { toast.error("Échec du visuel."); }
  };

  const removeFile = async (id) => {
    await api.delete(`/portal/files/${id}`);
    setFiles((prev) => prev.filter((f) => f.id !== id && f.folder_id !== id)); // dossier → son contenu part aussi
    toast.success("Élément supprimé.");
  };

  const moveFile = async (id, category) => {
    const fd = new FormData();
    fd.append("category", category);
    await api.patch(`/portal/files/${id}/move`, fd);
    setFiles((prev) => prev.filter((f) => f.id !== id));
    toast.success(`Déplacé vers « ${TABS.find((t) => t.k === category).label} ».`);
  };

  const markRead = async () => {
    await api.post(withCid("/portal/notifications/read"));
    setUnread(0); loadNotifs();
  };

  const bgUrl = bgId ? `${API}/portal/files/${bgId}/download?auth=${token()}` : null;
  const showUpload = isAdmin || tab === "actualite";

  return (
    <div className={`relative ${fullscreenBg ? "" : "min-h-[70vh] rounded-xl overflow-hidden border border-white/10"}`}>
      {/* Background layer */}
      <div className={fullscreenBg ? "fixed inset-0 z-0" : "absolute inset-0 z-0"}>
        {bgUrl ? <img src={bgUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0B2A52]" />}
        <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" />
      </div>

      <div className="relative z-10 p-6 md:p-8">
        {/* Tabs + bell */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <nav className="flex gap-1.5 flex-wrap" data-testid="tabs-nav">
            {TABS.map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-300 ${
                  tab === t.k ? "bg-gold text-ink" : "text-[#A0B2CE] hover:text-gold border border-white/10"
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={() => bgInput.current?.click()} data-testid="upload-bg-btn" title="Visuel de fond du client" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:border-gold transition-colors">
                <ImagePlus className="w-5 h-5 text-gold" />
              </button>
            )}
            <div className="relative">
              <button onClick={() => { setPanelOpen((v) => !v); if (!panelOpen && unread) markRead(); }} data-testid="notif-bell" className="relative w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:border-gold transition-colors">
                <Bell className="w-5 h-5 text-gold" />
                {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-gold text-ink text-[10px] font-mono flex items-center justify-center">{unread}</span>}
              </button>
              <AnimatePresence>
                {panelOpen && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="absolute right-0 mt-3 w-80 bg-[#0B2A52] border border-white/10 rounded-lg p-4 z-30 shadow-2xl" data-testid="notif-panel">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Notifications</p>
                      <button onClick={() => setPanelOpen(false)}><X className="w-4 h-4 text-[#A0B2CE]" /></button>
                    </div>
                    {notifs.length === 0 ? <p className="text-sm text-[#A0B2CE]">Aucune notification.</p> : (
                      <div className="space-y-3 max-h-72 overflow-auto">
                        {notifs.map((n) => (
                          <div key={n.id} className="border-b border-white/5 pb-3">
                            <p className="text-sm">{n.message}</p>
                            <p className="text-[11px] text-[#A0B2CE] mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <input ref={bgInput} type="file" accept="image/*" hidden onChange={(e) => uploadBackground(e.target.files[0])} data-testid="bg-input" />
        </div>

        {/* Upload zone */}
        {showUpload && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
            className="border border-dashed border-white/20 rounded-xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4 bg-white/[0.02]"
            data-testid="upload-zone"
          >
            <div className="flex items-center gap-4">
              <Upload className="w-6 h-6 text-gold" strokeWidth={1.3} />
              <div>
                <p className="font-serif text-lg">Déposer dans « {TABS.find((t) => t.k === tab).label} »</p>
                <p className="text-xs text-[#A0B2CE]">Glissez-déposez ou utilisez les boutons. Dans « Actualité », chaque dépôt est copié dans le Drive du client (« Dépôts ») et reste visible ici 5 jours.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="upload-file-btn" className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                <FileIcon className="w-4 h-4" /> {uploading ? (uploadLabel || "Envoi…") : "Fichier"}
              </button>
              <button onClick={createFolder} disabled={uploading} data-testid="upload-folder-btn" className="inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                <FolderPlus className="w-4 h-4" /> Dossier
              </button>
            </div>
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => uploadFiles(e.target.files)} data-testid="file-input" />
            <input ref={folderInput} type="file" multiple hidden {...{ webkitdirectory: "", directory: "" }}
              onChange={(e) => { uploadFolder(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="folder-input" />
          </div>
        )}

        {/* Recherche */}
        <div className="mb-6">
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-folder-search"
            placeholder="Rechercher un fichier par nom…"
            className="w-full md:max-w-md bg-white/[0.03] border border-white/10 focus:border-gold outline-none rounded-lg px-4 py-2.5 text-sm transition-colors" />
        </div>

        {/* Documents Drive */}
        <div className="mb-10" data-testid="admin-drive-section">
          <div className="flex items-center flex-wrap gap-2 mb-4">
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold">Documents Drive</p>
            <span className="text-[#5b606e]">·</span>
            <button onClick={() => driveJumpTo(-1)} className="font-mono text-[11px] text-[#A0B2CE] hover:text-gold transition-colors">
              {TABS.find((t) => t.k === tab)?.label}
            </button>
            {drivePath.map((p, i) => (
              <span key={p.id} className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-[#5b606e]" />
                <button onClick={() => driveJumpTo(i)} className="font-mono text-[11px] text-[#A0B2CE] hover:text-gold transition-colors max-w-[180px] truncate">{p.name}</button>
              </span>
            ))}
          </div>
          {driveLoading ? (
            <p className="font-mono text-xs tracking-[0.3em] text-gold animate-pulse">CHARGEMENT DRIVE…</p>
          ) : driveItems.length === 0 ? (
            <div className="border border-white/10 rounded-xl p-8 text-center"><p className="text-[#A0B2CE] text-sm">Aucun élément Drive pour cet onglet.</p></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="admin-drive-list">
              {driveItems.filter((it) => it.name.toLowerCase().includes(search.toLowerCase())).map((it) => (
                it.isFolder ? (
                  <div key={it.id} className="group border border-white/10 rounded-lg p-5 flex items-center gap-4 bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
                    <button onClick={() => openDriveFolder(it)} data-testid={`admin-drive-folder-${it.id}`} className="flex items-center gap-4 min-w-0 flex-1 text-left">
                      <div className="w-11 h-11 rounded-md bg-gold/10 flex items-center justify-center shrink-0"><Folder className="w-5 h-5 text-gold" /></div>
                      <div className="min-w-0 flex-1"><p className="truncate group-hover:text-gold transition-colors">{it.name}</p><p className="text-[11px] text-[#A0B2CE] font-mono">Dossier</p></div>
                    </button>
                    {drivePath.length === 0 && (
                      <a href={`${API}/portal/folders/${tab}/download-zip?folder_id=${it.id}&auth=${token()}${clientId ? `&client_id=${clientId}` : ""}`}
                        data-testid={`admin-download-subfolder-${it.id}`} title="Télécharger ce dossier (.zip)"
                        className="w-9 h-9 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors shrink-0">
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    <ChevronRight className="w-4 h-4 text-[#A0B2CE] group-hover:text-gold shrink-0" />
                  </div>
                ) : (
                  <button key={it.id} onClick={() => setPreview({ id: it.id, name: it.name, mimeType: it.mimeType, driveUrl: driveFileUrl(it.id) })} data-testid={`admin-drive-file-${it.id}`}
                    className="group border border-white/10 rounded-lg p-5 flex items-center gap-4 bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-left w-full">
                    <div className="w-11 h-11 rounded-md bg-gold/10 flex items-center justify-center shrink-0"><FileIcon className="w-5 h-5 text-gold" /></div>
                    <div className="min-w-0 flex-1"><p className="truncate group-hover:text-gold transition-colors">{it.name}</p><p className="text-[11px] text-[#A0B2CE] font-mono">{it.size ? humanSize(it.size) : "Document"}</p></div>
                    <Download className="w-4 h-4 text-[#A0B2CE] group-hover:text-gold shrink-0" />
                  </button>
                )
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center flex-wrap gap-2 mb-4">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold">Déposés dans l'application</p>
          {portalFolder && (
            <>
              <span className="text-[#5b606e]">·</span>
              <button onClick={() => setPortalFolder(null)} data-testid="portal-folder-back"
                className="font-mono text-[11px] text-[#A0B2CE] hover:text-gold transition-colors">
                Tous les dépôts
              </button>
              <ChevronRight className="w-3 h-3 text-[#5b606e]" />
              <span className="font-mono text-[11px] text-gold flex items-center gap-1.5" data-testid="portal-folder-current">
                <Folder className="w-3 h-3" /> {portalFolder.name}
              </span>
              <a href={`${API}/portal/deposit-folders/${portalFolder.id}/download-zip?auth=${token()}`}
                data-testid="portal-folder-zip" title="Télécharger tout le dossier (.zip)"
                className="w-7 h-7 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors">
                <Download className="w-3.5 h-3.5" />
              </a>
            </>
          )}
        </div>
        {/* Files */}
        {loading ? (
          <p className="font-mono text-xs tracking-[0.3em] text-gold animate-pulse">CHARGEMENT…</p>
        ) : (() => {
          const visible = (portalFolder
            ? files.filter((f) => !f.is_folder && f.folder_id === portalFolder.id)
            : files.filter((f) => f.is_folder || !f.folder_id)
          ).filter((f) => f.original_filename.toLowerCase().includes(search.toLowerCase()));
          if (visible.length === 0) {
            return (
              <div className="border border-white/10 rounded-xl p-14 text-center" data-testid="empty-state">
                <p className="text-[#A0B2CE]">{portalFolder ? "Ce dossier est vide." : "Aucun document dans cet onglet pour le moment."}</p>
              </div>
            );
          }
          return (
          <div className="grid md:grid-cols-2 gap-4" data-testid="files-list">
            {visible.map((f) => (
              <motion.div key={f.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="group border border-white/10 rounded-lg p-5 flex items-center gap-4 bg-white/[0.03] hover:bg-white/[0.06] transition-colors" data-testid={`file-${f.id}`}>
                {f.is_folder ? (
                  <button onClick={() => setPortalFolder({ id: f.id, name: f.original_filename })} data-testid={`open-folder-${f.id}`}
                    className="w-11 h-11 rounded-md bg-gold/10 flex items-center justify-center shrink-0 hover:bg-gold/25 transition-colors" title="Ouvrir le dossier">
                    <Folder className="w-5 h-5 text-gold" />
                  </button>
                ) : (
                  <FileThumb file={f} onClick={() => setPreview(f)} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate cursor-pointer hover:text-gold transition-colors"
                    onClick={() => f.is_folder ? setPortalFolder({ id: f.id, name: f.original_filename }) : setPreview(f)}>{f.original_filename}</p>
                  <p className="text-[11px] text-[#A0B2CE] font-mono">
                    {f.is_folder
                      ? `Dossier · ${files.filter((x) => x.folder_id === f.id).length} fichier(s)`
                      : humanSize(f.size)} · {new Date(f.created_at).toLocaleDateString("fr-FR")}
                  </p>
                  {(f.uploaded_by_name || f.uploaded_by) && (
                    <p className="text-[11px] text-gold/70 font-mono truncate" data-testid={`uploader-${f.id}`}>Déposé par {f.uploaded_by_name || f.uploaded_by}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {f.is_folder ? (
                    <a href={`${API}/portal/deposit-folders/${f.id}/download-zip?auth=${token()}`}
                      data-testid={`zip-folder-${f.id}`} title="Télécharger tout le dossier (.zip)"
                      className="w-9 h-9 rounded-md flex items-center justify-center text-[#A0B2CE] hover:text-gold hover:bg-white/5 transition-colors">
                      <Download className="w-4 h-4" />
                    </a>
                  ) : (
                    <a href={`${API}/portal/files/${f.id}/download?auth=${token()}`} target="_blank" rel="noopener noreferrer" title="Télécharger" data-testid={`download-${f.id}`} className="w-9 h-9 rounded-md flex items-center justify-center text-[#A0B2CE] hover:text-gold hover:bg-white/5 transition-colors">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  <select onChange={(e) => e.target.value && moveFile(f.id, e.target.value)} value="" title="Déplacer vers" data-testid={`move-${f.id}`} className="bg-transparent text-[#A0B2CE] text-xs font-mono cursor-pointer hover:text-gold outline-none">
                    <option value="" className="bg-[#0B2A52]">Déplacer…</option>
                    {TABS.filter((t) => t.k !== f.category).map((t) => (
                      <option key={t.k} value={t.k} className="bg-[#0B2A52]">{t.label}</option>
                    ))}
                  </select>
                  <button onClick={() => removeFile(f.id)} title="Supprimer" data-testid={`delete-${f.id}`} className="w-9 h-9 rounded-md flex items-center justify-center text-[#A0B2CE] hover:text-red-400 hover:bg-white/5 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          );
        })()}
      </div>
      <FilePreviewModal file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
